import React, { useState } from "react";

interface EffectCardProps {
  title: string;
  description: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const EffectCard: React.FC<EffectCardProps> = ({
  title,
  description,
  className = "",
  style = {},
  children,
}) => {
  const [showCopied, setShowCopied] = useState(false);

  const copyToClipboard = () => {
    // Extract the CSS class or style for copying
    const copyText = className || JSON.stringify(style, null, 2);
    navigator.clipboard.writeText(copyText).then(() => {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1000);
    });
  };

  return (
    <div className="component-item">
      <div className="component-preview">
        <div className={`ds-effect-demo ${className}`} style={style}>
          {children}
        </div>
        <div className={`ds-copy-indicator ${showCopied ? "show" : ""}`}>
          Copied!
        </div>
      </div>
      <div className="component-info">
        <h4>{title}</h4>
        <p>{description}</p>
        {className && (
          <div className="component-code" onClick={copyToClipboard}>
            <code>className="{className}"</code>
          </div>
        )}
        {!className && Object.keys(style).length > 0 && (
          <div className="component-code" onClick={copyToClipboard}>
            <code>style={JSON.stringify(style, null, 2)}</code>
          </div>
        )}
      </div>
    </div>
  );
};

export const Effects: React.FC = () => {
  return (
    <div className="ds-section">
      <div className="ds-section-header">
        <h2>✨ Effects</h2>
        <p className="ds-description">
          Visual effects including shadows, textures, and materials. Click any
          effect to copy its implementation.
        </p>
      </div>

      <div className="component-category">
        <h3 className="ds-section-title">Drop Shadows</h3>
        <div className="component-showcase">
          <EffectCard
            title="Standard Drop Shadow"
            description="Main Excalidraw element drop shadow effect"
            className="excalidraw"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
            }}
          >
            Drop Shadow
          </EffectCard>

          <EffectCard
            title="Subtle Drop Shadow"
            description="Light drop shadow for UI components"
            className="subtle-shadow"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
            }}
          >
            Subtle Shadow
          </EffectCard>

          <EffectCard
            title="Island Shadow"
            description="Standard UI panel shadow (var(--shadow-island))"
            className="island-shadow"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
            }}
          >
            Island Shadow
          </EffectCard>

          <EffectCard
            title="Modal Shadow"
            description="Heavy shadow for modal dialogs (var(--modal-shadow))"
            className="modal-shadow"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
            }}
          >
            Modal Shadow
          </EffectCard>
        </div>
      </div>

      <div className="component-category">
        <h3 className="ds-section-title">Textures</h3>
        <div className="component-showcase">
          <EffectCard
            title="Standard Texture"
            description="Standard noise texture (85% opacity)"
            className="texture-standard"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
              border: "1px solid var(--color-surface-high)",
            }}
          >
            Standard Texture
          </EffectCard>

          <EffectCard
            title="Subtle Texture"
            description="Medium noise texture (60% opacity)"
            className="texture-subtle"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
              border: "1px solid var(--color-surface-high)",
            }}
          >
            Subtle Texture
          </EffectCard>

          <EffectCard
            title="Contrast Texture"
            description="Full opacity noise texture (100% opacity)"
            className="texture-contrast"
            style={{
              width: "200px",
              height: "120px",
              backgroundColor: "var(--color-surface-lowest)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "var(--color-on-surface)",
              border: "1px solid var(--color-surface-high)",
            }}
          >
            Contrast Texture
          </EffectCard>
        </div>
      </div>

      <div className="component-category">
        <h3 className="ds-section-title">Materials</h3>
        <div className="component-showcase">
          <EffectCard
            title="Paper Material"
            description="Paper-like material with texture, inner shadow, and drop shadow"
            className="material-paper"
            style={{
              width: "200px",
              height: "120px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              fontWeight: "500",
              color: "var(--color-on-surface)",
            }}
          >
            Paper
          </EffectCard>
        </div>
      </div>
    </div>
  );
};
